// Persisted form of the non-interactive aggregate (`PostCaseNonInteractiveIps`).
//
// Extracted from CaseDetail in v1.148.9. It lived inline as two closures, which is why
// both of its bugs shipped unseen: it is pure size arithmetic over a payload shape, the
// one kind of logic that is cheap to test and impossible to eyeball. Tests live in
// `frontend/scripts/nonInteractivePersist.test.mjs` (run: `node scripts/…` from frontend/).
//
// 🔴 THE BUDGET IS ~32,768 CHARACTERS, NOT 48,000. An Azure Table Storage string
// property is capped at 64 KB of UTF-16, i.e. ~32,768 CHARS, and exceeding it 400s the
// ENTIRE entity write - it does not truncate. v1.147.0 guarded at 48,000, so any payload
// between the two sailed past the guard and failed the write, and the fail-soft catch
// logged to the console only. Symptom: the analyst runs the query, sees 59 IPs, refreshes,
// and the whole thing is gone with nothing said. This is the SECOND time this cap has bitten
// this feature (v1.98.2 did the same with ipEvidence) - see feedback_table_storage_64kb_property_limit.
//
// So: trim to a BUDGET, in priority tiers, and never all-or-nothing. Dropping the whole
// aggregate to protect a size limit throws away recovered IOCs; dropping a user-agent
// string does not. Every IP is kept at every tier - the IP set IS the deliverable.
export const NI_PERSIST_BUDGET = 30000;

// Reserve for the note the restore loop writes: those characters are not in the running
// total, and discovering the overrun at the final guard would save NOTHING.
const NI_NOTE_HEADROOM = 300;

const TIER_LABELS = [
  null,
  'client and country detail',
  'resource breakdown',
  'failure codes',
  'device and session detail',
];

// Exact character cost of adding one property to an object that already has at least
// one: comma + quoted key + colon + serialized value. Exact, because JSON.stringify
// emits no whitespace - which is what lets the restore loop below fill the leftover
// budget without re-serializing the whole form once per row. An `undefined` value
// costs nothing because JSON.stringify omits the property entirely.
export const addedJsonLength = (key, value) => (
  value === undefined ? 0 : 1 + JSON.stringify(key).length + 1 + JSON.stringify(value).length
);

export function buildNonInteractivePersistForm(payload, tier, nowIso) {
  // tier 0 = everything; each step sheds the least valuable fields still present.
  // 🔴 TIER 0 MUST BE LOSSLESS. It shipped capping sessions at 4 and apps at 6 while the
  // endpoint returns 8 and 12, so a reload silently dropped half the sessions - AND no
  // "saved in reduced form" banner fired, because that only triggers when `tier > 0`.
  // The panel therefore showed different data depending on whether you had just run the
  // query or come back to it, with nothing said. These four numbers must track the
  // endpoint's own ConvertTo-CountedList / ConvertTo-StringList caps in
  // PostCaseNonInteractiveIps: apps 12, sessions 8, errors 10, devices 8.
  const capApps    = tier <= 0 ? 12 : tier === 1 ? 6 : tier === 2 ? 4 : 3;
  const capErrs    = tier <= 1 ? 10 : tier === 2 ? 6 : 4;
  const capDevs    = tier <= 2 ? 8  : tier === 3 ? 4 : 2;
  const capSess    = tier <= 1 ? 8  : tier === 2 ? 4 : 2;
  const keepVerbose = tier <= 0;   // userAgents / countries / trust+token types / legacy apps
  const keepAppCts  = tier <= 2;
  const keepErrs    = tier <= 3;

  return {
    fetchedUtc:      nowIso || new Date().toISOString(),
    window:          payload.window || null,
    coverage:        payload.coverage || null,
    failureCoverage: payload.failureCoverage || null,
    summary:         payload.summary || null,
    devices: (payload.devices || []).slice(0, 40).map((d) => ({
      deviceId: d.deviceId, name: d.name, trustType: d.trustType,
      registeredUtc: d.registeredUtc, osVersion: d.osVersion,
      inRegistry: d.inRegistry, events: d.events,
      firstSeen: d.firstSeen, lastSeen: d.lastSeen,
      ips: (d.ips || []).slice(0, 10),
    })),
    ips: (payload.ips || []).slice(0, 300).map((r) => {
      const row = {
        // Tier A - always kept. Identity, volume, timing, network, containment.
        ip: r.ip, events: r.events, successes: r.successes,
        withDevice: r.withDevice, noDevice: r.noDevice,
        firstSeen: r.firstSeen, lastSeen: r.lastSeen, activeDays: r.activeDays,
        asn: r.asn, provider: r.provider, networkType: r.networkType,
        riskLevel: r.riskLevel, enriched: r.enriched,
        sawRevokedGrant: r.sawRevokedGrant, lastSuccess: r.lastSuccess,
        // Tier B - the primary tell and the session join.
        devices:   (r.devices || []).slice(0, capDevs),
        deviceIds: (r.deviceIds || []).slice(0, capDevs),
        sessionIds: (r.sessionIds || []).slice(0, capSess),
      };
      if (keepErrs)   row.errorCodes = (r.errorCodes || []).slice(0, capErrs);
      if (keepAppCts) row.appCounts  = (r.appCounts  || []).slice(0, capApps);
      if (keepVerbose) {
        row.trustTypes = (r.trustTypes || []).slice(0, 4);
        row.tokenTypes = (r.tokenTypes || []).slice(0, 4);
        row.countries  = (r.countries  || []).slice(0, 4);
        row.apps       = (r.apps       || []).slice(0, 6);
        row.userAgents = (r.userAgents || []).slice(0, 6).map((ua) => String(ua).slice(0, 120));
      }
      return row;
    }),
  };
}

// Highest value first, and the order is set by MEASURED data, not by the order the fields
// were listed in the handover note.
//   1. outcome    - `sawRevokedGrant` + `lastSuccess`, two scalars, ~45 chars/row, and the
//                   thing that decides whether a token was cut off.
//   2. failure codes - the EVIDENCE behind that verdict, and the cheaper of the two lists:
//                   they exist on only 29 of 59 rows on the reference case (~70 chars each),
//                   because most IPs never failed. On that case they carry the whole
//                   containment story - attacker 43.165.1.42 `50173x6`, last success
//                   2026-08-03; the victim's own office IP `50173x115`, still succeeding on
//                   08-18. Ordering these AFTER session ids left them on ONE row of 59
//                   (v1.148.9, caught on the first real render), throwing away the finding
//                   to keep a corroborator.
//   3. session ids - the join to the case's audited timeline. Present on EVERY row and
//                   ~165 chars each, so it is the expensive one; and it is corroboration,
//                   never attribution (a stolen token keeps the victim's session id).
//   4. device names - the primary attribution rule, already partly covered by the one
//                   device the stripped row keeps.
const RESTORE_PASSES = [
  {
    label: 'outcome',
    has:   (r) => r.sawRevokedGrant !== undefined || r.lastSuccess !== undefined,
    cost:  (r) => addedJsonLength('sawRevokedGrant', r.sawRevokedGrant)
                + addedJsonLength('lastSuccess', r.lastSuccess),
    apply: (dst, r) => { dst.sawRevokedGrant = r.sawRevokedGrant; dst.lastSuccess = r.lastSuccess; },
  },
  {
    label: 'failure codes',
    has:   (r) => (r.errorCodes || []).length > 0,
    cost:  (r) => addedJsonLength('errorCodes', (r.errorCodes || []).slice(0, 4)),
    apply: (dst, r) => { dst.errorCodes = (r.errorCodes || []).slice(0, 4); },
  },
  {
    label: 'session ids',
    has:   (r) => (r.sessionIds || []).length > 0,
    cost:  (r) => addedJsonLength('sessionIds', (r.sessionIds || []).slice(0, 4)),
    apply: (dst, r) => { dst.sessionIds = (r.sessionIds || []).slice(0, 4); },
  },
  {
    label: 'device names',
    has:   (r) => (r.devices || []).length > 1,
    // Replaces the single device already on the row, so only the delta is charged.
    cost:  (r) => addedJsonLength('devices', (r.devices || []).slice(0, 4))
                - addedJsonLength('devices', (r.devices || []).slice(0, 1)),
    apply: (dst, r) => { dst.devices = (r.devices || []).slice(0, 4); },
  },
];

/**
 * Reduce the aggregate to something that will fit a single Table Storage property.
 * Returns { serialized, persist } - `persist` is null when nothing was shed, and
 * `serialized` is null when even the leanest form does not fit (do NOT attempt the
 * write in that case: it would 400 and take the whole case update with it).
 */
export function buildNonInteractivePersist(payload, nowIso) {
  if (!payload || !Array.isArray(payload.ips)) return { serialized: '', persist: null };

  let tier = 0;
  let form = buildNonInteractivePersistForm(payload, tier, nowIso);
  let serialized = JSON.stringify(form);

  while (serialized.length > NI_PERSIST_BUDGET && tier < TIER_LABELS.length - 1) {
    tier += 1;
    form = buildNonInteractivePersistForm(payload, tier, nowIso);
    serialized = JSON.stringify(form);
  }

  let persist = null;

  if (serialized.length > NI_PERSIST_BUDGET) {
    // Even the leanest tier does not fit. Keep the IP set - it is the deliverable - and
    // say plainly that detail was dropped, rather than saving nothing.
    //
    // 🔴 THEN SPEND WHAT IS LEFT OF THE BUDGET (v1.148.9). The strip alone lands MIL-MAR
    // at 22,045 of 30,000 with EVERY per-IP detail gone, so ~8k of budget went unused and
    // session linkage - the whole point of v1.148.0, and the only thing tying the
    // recovered IPs to the audited half of the intrusion - was absent after a reload on
    // the very case that needs it. Detail is restored a FIELD AT A TIME ACROSS ALL ROWS,
    // not row-by-row to exhaustion, so the rows stay comparable: "session ids for 59 of
    // 59 IPs" is a claim the analyst can check, "full detail for the first twelve" is not.
    //
    // Two strip shapes, because ~100 IPs of the leaner-but-not-lean shape is already over
    // the cap: measured, a stripped row is ~300 chars, so a case with 100+ non-interactive
    // IPs used to save NOTHING and show "too large to save". The IP set is the deliverable
    // - an analyst can re-run for detail but cannot re-derive which addresses were found -
    // so there is one more step below "keep a bit of detail for every IP": keep only the
    // addresses and their shape. Giving up comes after that, not instead of it.
    const src = (payload.ips || []).slice(0, 300);
    const STRIP_SHAPES = [
      {
        dropped: 'most per-IP detail',
        row: (r) => ({
          ip: r.ip, events: r.events, successes: r.successes,
          withDevice: r.withDevice, noDevice: r.noDevice,
          firstSeen: r.firstSeen, lastSeen: r.lastSeen, activeDays: r.activeDays,
          asn: r.asn, provider: r.provider, networkType: r.networkType,
          riskLevel: r.riskLevel, enriched: r.enriched,
          devices: (r.devices || []).slice(0, 1),
        }),
      },
      {
        dropped: 'everything except the IP list itself',
        row: (r) => ({
          ip: r.ip, events: r.events, successes: r.successes,
          firstSeen: r.firstSeen, lastSeen: r.lastSeen, asn: r.asn,
        }),
      },
    ];

    let shape = STRIP_SHAPES[0];
    for (let s = 0; s < STRIP_SHAPES.length; s += 1) {
      shape = STRIP_SHAPES[s];
      form.ips = src.map(shape.row);
      form.persist = { reduced: true, dropped: shape.dropped, note: 'Re-run to see full detail.' };
      serialized = JSON.stringify(form);
      if (serialized.length <= NI_PERSIST_BUDGET - NI_NOTE_HEADROOM) break;
    }

    const ceiling = NI_PERSIST_BUDGET - NI_NOTE_HEADROOM;
    let used = serialized.length;
    const restored = [];
    RESTORE_PASSES.forEach((pass) => {
      let n = 0;
      form.ips.forEach((dst, i) => {
        const r = src[i];
        if (!r || !pass.has(r)) return;
        const cost = pass.cost(r);
        if (used + cost > ceiling) return;
        pass.apply(dst, r);
        used += cost;
        n += 1;
      });
      if (n > 0) restored.push(`${pass.label} for ${n} of ${form.ips.length} IPs`);
    });

    if (restored.length > 0) {
      form.persist.note = `Kept ${restored.join(', ')}. Re-run to see full detail.`;
    }
    serialized = JSON.stringify(form);
    persist = form.persist;
  } else if (tier > 0) {
    form.persist = { reduced: true, dropped: TIER_LABELS.slice(1, tier + 1).join(', '), note: 'Re-run to see full detail.' };
    serialized = JSON.stringify(form);
    persist = form.persist;
  }

  if (serialized.length > NI_PERSIST_BUDGET) {
    // Nothing left to shed. The caller must not attempt the write.
    return { serialized: null, persist: null, oversizeChars: serialized.length };
  }

  return { serialized, persist };
}
